/** Minimal shape of a schema field needed to find its referenced file ids. */
export interface FileIdBearingField {
  type?: string;
  arrayType?: string;
  defaultValue?: string;
  templateFile?: string;
}

/**
 * Collect every ManagedFile id referenced by a set of schema fields:
 * - a single-file field's `defaultValue`,
 * - an Excel output field's `templateFile`,
 * - each id inside a file-array field's JSON-array `defaultValue`.
 *
 * Used to resolve saved ids back to filenames for display (the id→name map is
 * otherwise only populated when the user picks a file in the current session).
 */
export const collectFieldFileIds = (fields: FileIdBearingField[]): string[] => {
  const ids = new Set<string>();
  fields.forEach(field => {
    const isFileField =
      field.type === 'file' || (field.type === 'array' && field.arrayType === 'file');
    if (!isFileField) return;

    if (field.templateFile) {
      ids.add(field.templateFile);
    }

    if (field.defaultValue) {
      if (field.type === 'array' && field.arrayType === 'file') {
        try {
          const parsed = JSON.parse(field.defaultValue);
          if (Array.isArray(parsed)) {
            parsed.forEach(id => {
              if (typeof id === 'string' && id) ids.add(id);
            });
          }
        } catch {
          // defaultValue isn't a JSON array — nothing to collect.
        }
      } else {
        ids.add(field.defaultValue);
      }
    }
  });
  return Array.from(ids);
};
